import { useEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { MachineDetail } from "@/types/lines.types";

export interface NewMachineBasic {
  name: string;
  code: string;
  type: "MACHINE" | "EQUIPMENT" | "HUMAN";
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  machines: MachineDetail[];
  usedMachineIds: Set<string>;
  onSelect: (machine: MachineDetail) => void;
  onCreateNew?: (basic: NewMachineBasic) => void;
}

const CODE_REGEX = /^[A-Za-z0-9_-]+$/;

export function AddMachinePopover({
  open,
  onOpenChange,
  machines,
  usedMachineIds,
  onSelect,
  onCreateNew,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<NewMachineBasic>({ name: "", code: "", type: "MACHINE" });

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onOpenChange(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const available = machines.filter((m) => !usedMachineIds.has(m.id));
  const allUsed = available.length === 0;
  const codeTrim = form.code.trim();
  const codeAlreadyUsed = codeTrim.length > 0 && machines.some((m) => m.code === codeTrim);
  const canCreate =
    !!onCreateNew &&
    form.name.trim().length > 0 &&
    codeTrim.length > 0 &&
    CODE_REGEX.test(codeTrim) &&
    !codeAlreadyUsed;

  function submitNew() {
    if (!onCreateNew || !canCreate) return;
    onCreateNew({
      name: form.name.trim(),
      code: form.code.trim(),
      type: form.type,
    });
    onOpenChange(false);
  }

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Adicionar máquina ao canvas"
      className="absolute left-0 top-full z-20 mt-1 w-80 rounded-md border bg-card shadow-lg"
    >
      <div className="flex items-center justify-between border-b px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <span>{creating ? "Nova máquina" : "Adicionar máquina"}</span>
        {creating ? (
          <button
            type="button"
            onClick={() => setCreating(false)}
            className="text-text-tertiary hover:text-text-primary"
            aria-label="Voltar"
          >
            <X size={14} />
          </button>
        ) : null}
      </div>

      {creating ? (
        <div className="space-y-3 px-3 py-3">
          <div className="space-y-1.5">
            <Label htmlFor="popover-machine-name">Nome</Label>
            <Input
              id="popover-machine-name"
              // eslint-disable-next-line jsx-a11y/no-autofocus -- popover modal: foco imediato no primeiro campo é UX esperado
              autoFocus
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Ex.: Mistura"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="popover-machine-code">Código</Label>
            <Input
              id="popover-machine-code"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder="Ex.: M1"
              aria-invalid={form.code.length > 0 && !CODE_REGEX.test(form.code)}
            />
            {form.code.length > 0 && !CODE_REGEX.test(form.code) ? (
              <p className="text-[11px] text-rd-danger-700">Use apenas letras, números, _ ou -.</p>
            ) : codeAlreadyUsed ? (
              <p className="text-[11px] text-rd-danger-700">
                Já existe uma máquina com este código nesta linha.
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="popover-machine-type">Tipo</Label>
            <select
              id="popover-machine-type"
              value={form.type}
              onChange={(e) =>
                setForm((f) => ({ ...f, type: e.target.value as NewMachineBasic["type"] }))
              }
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
            >
              <option value="MACHINE">Máquina</option>
              <option value="EQUIPMENT">Equipamento</option>
              <option value="HUMAN">Humano</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
              Cancelar
            </Button>
            <Button size="sm" disabled={!canCreate} onClick={submitNew}>
              Adicionar
            </Button>
          </div>
        </div>
      ) : (
        <>
          {allUsed ? (
            <div className="px-3 py-3 text-sm text-text-secondary">
              {onCreateNew
                ? "Ainda não há máquinas. Cadastre a primeira para começar o fluxo."
                : "Todas as máquinas já estão no canvas — cadastre uma nova primeiro."}
            </div>
          ) : (
            <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
              {available.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected="false"
                    onClick={() => {
                      onSelect(m);
                      onOpenChange(false);
                    }}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors hover:bg-bg-muted-rd"
                  >
                    <span className="font-medium text-text-primary">{m.name}</span>
                    <span className="font-mono text-xs text-text-tertiary">{m.code}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {onCreateNew ? (
            <div className="border-t px-3 py-2">
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex w-full items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-900"
              >
                <Plus size={14} />
                Cadastrar nova máquina
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
